/**
 * Top-level game controller. Owns:
 *  - the Pixi Application (rendering)
 *  - the State (game data)
 *  - the EventBus (UI ↔ sim communication)
 *  - the BuildPhase / WavePhase controllers
 *
 * Main loop is fixed-step at SIM_HZ; render runs each Pixi tick.
 */

import { Application, Container, Ticker } from "pixi.js";
import {
  State,
  emptyState,
  allDrawsPlaced,
  pickInitialTowerTargeting,
  type TargetingPriority,
} from "./State";
import { EventBus } from "../events/EventBus";
import { attachTelemetry } from "../telemetry";
import { RNG } from "./rng";
import { BASE, Cell, GRID_W, GRID_H, isBuildable } from "../data/map";
import { findRoute, flattenRoute, buildAirRoute } from "../systems/Pathfinding";
import {
  spawnContainerPayload as spawnPayload,
  drainPendingSpawns as drainPayload,
  type PendingSpawn,
} from "../systems/PayloadSpawner";
import {
  BoardLayers,
  makeBoardLayers,
  renderGround,
  renderPathTrace,
  renderCheckpoints,
} from "../render/BoardRenderer";
import {
  FINE_TILE,
  START_GOLD,
  START_LIVES,
  SIM_DT,
  SPEEDS,
  type SpeedMultiplier,
} from "./constants";
import { BuildPhase } from "../controllers/BuildPhase";
import { WavePhase } from "../controllers/WavePhase";
import { WAVES, type WaveDef } from "../data/waves";
import {
  MAZE_BLUEPRINT,
  MAZE_REMOVALS,
  MAZE_KEEPER_INDICES,
} from "../data/maze-blueprint";
import { exposureAt } from "../sim/blueprintKeeper";
import {
  CHANCE_TIER_UPGRADE_COST,
  MAX_CHANCE_TIER,
  RUNES_ENABLED,
} from "./constants";
import { COMBOS, COMBO_BY_NAME, nextUpgrade } from "../data/combos";
import { GEM_TYPES, type GemType, type Quality } from "../render/theme";
import { Combat, invalidateTowerStats } from "../systems/Combat";
import { Traps } from "../systems/Traps";
import { VfxRenderer } from "../render/VfxRenderer";
import { TowerSpriteCache } from "../render/TowerRenderer";
import {
  renderTowers,
  renderRocks,
  renderCreeps,
  renderProjectiles,
  renderBeams,
  renderHover,
  renderRangePreview,
  renderDrawPartnerHighlight,
  renderSelectedTowerPartnerHighlight,
  renderAiOverlay,
  setAiHighlight,
  setAiCombo,
} from "../render/EntityRenderer";
import { renderCursorGrid, renderUniformGrid } from "../render/CursorGrid";
import {
  type Blueprint,
  computeKeeperIndices,
  renderBlueprintOverlay,
} from "../render/BlueprintOverlay";
import { AISpectatorDriver } from "../controllers/AISpectatorDriver";

export class Game {
  readonly bus = new EventBus();
  readonly state: State;
  readonly rng: RNG;
  seed: number;
  readonly app: Application;

  readonly board: Container;
  readonly layers: BoardLayers;
  readonly towerSprites: TowerSpriteCache;

  private buildPhase: BuildPhase;
  private wavePhase: WavePhase;
  private combat: Combat;
  private traps: Traps;
  private vfx: VfxRenderer;

  /** Accumulator for fixed-step sim. */
  private accum = 0;
  private lastTickTime = performance.now();
  private backgroundInterval: ReturnType<typeof setInterval> | null = null;
  private nextEntityId = 1;
  private pendingPayloadSpawns: PendingSpawn[] = [];

  /** Tile under the pointer (for build preview). null if outside the board. */
  hoverTile: { x: number; y: number } | null = null;

  /** Raw pointer position in board-space pixels. null if outside the board. */
  hoverPixel: { x: number; y: number } | null = null;

  /** Whether the pointer is currently over the canvas. */
  hoverPresent = false;

  /** Animated halo opacity (0..1). Fades in on pointer-enter, out on pointer-leave. */
  haloAlpha = 0;

  /** Last known hover positions — kept alive during fade-out so the grid doesn't vanish instantly. */
  private lastHoverPixel: { x: number; y: number } | null = null;
  private lastHoverTile: { x: number; y: number } | null = null;

  /** Whether the cursor-local grid feature is enabled (tweaks toggle). */
  cursorGridEnabled = true;

  /** Whether the path overlay is visible. Persisted to localStorage. */
  pathVizEnabled = true;

  /** Blueprint overlay mode — hidden cheat activated by Ctrl+B. */
  blueprintMode = false;
  private blueprint: Blueprint | null = null;

  /** Creative mode — hidden cheat (Ctrl+Shift+C) to place rocks freely during build phase. */
  creativeMode = false;

  /** AI spectator mode — AI plays while user watches. */
  aiSpectator = false;
  aiDriver: AISpectatorDriver | null = null;

  /** True during AI recording pass — suppresses render-only side effects. */
  _recording = false;

  private detachTelemetry?: () => void;

  /** Currently selected tower (if any). null otherwise. */
  selectedTowerId: number | null = null;

  /** Tower hovered in leaderboard — shows a subtle highlight on the board. */
  hoveredTowerId: number | null = null;

  /** Currently selected rock anchor id (mutually exclusive with selectedTowerId). */
  selectedRockId: number | null = null;

  /** Currently selected creep id (mutually exclusive with tower/rock). */
  selectedCreepId: number | null = null;

  constructor(app: Application) {
    this.app = app;
    this.seed = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
    this.rng = new RNG(this.seed);

    const grid: Cell[][] = BASE.grid.map((row) => row.slice());
    this.state = emptyState(grid, WAVES.length);

    this.board = new Container();
    this.board.label = "board";
    this.layers = makeBoardLayers();
    this.board.addChild(this.layers.root);
    this.app.stage.addChild(this.board);

    this.towerSprites = new TowerSpriteCache(this.app.renderer, this.bus);

    const storedPathViz = localStorage.getItem("gemtd:path-viz");
    if (storedPathViz !== null) this.pathVizEnabled = storedPathViz === "1";

    this.state.speed = this.loadStoredSpeed();

    this.buildPhase = new BuildPhase(this);
    this.wavePhase = new WavePhase(this);
    this.combat = new Combat(this);
    this.traps = new Traps(this);
    this.vfx = new VfxRenderer(this.bus);

    renderGround(this.layers.ground, this.state.grid);
    renderCheckpoints(this.layers.checkpoints);
    this.refreshRoute();

    this.app.ticker.add(this.tick, this);

    this.bus.on("ai:highlight", (h) => setAiHighlight(h));
    this.bus.on("ai:combo", ({ towerIds }) => setAiCombo(towerIds));
    this.bus.on("ai:clear", () => {
      setAiHighlight(null);
      setAiCombo(null);
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.backgroundInterval = setInterval(
          () => this.drainAccumulator(),
          1000,
        );
      } else {
        if (this.backgroundInterval !== null) {
          clearInterval(this.backgroundInterval);
          this.backgroundInterval = null;
        }
      }
    });
  }

  start(): void {
    this.state.phase = "title";
    this.state.lives = START_LIVES;
    this.state.gold = START_GOLD;
    this.state.wave = 0;
    this.bus.emit("phase:enter", { phase: "title" });
    this.bus.emit("gold:change", { gold: this.state.gold });
    this.bus.emit("lives:change", { lives: this.state.lives });
  }

  /** Kick off a new run from the title screen. */
  newGame(aiSpectator = false): void {
    this.aiSpectator = aiSpectator;
    this.aiDriver = aiSpectator ? new AISpectatorDriver() : null;
    this.state.towers = [];
    this.state.rocks = [];
    this.state.creeps = [];
    this.state.projectiles = [];
    this.vfx.clear();
    this.state.undoStack = [];
    this.state.draws = [];
    this.state.activeDrawSlot = null;
    this.state.designatedKeepTowerId = null;
    this.state.chanceTier = 0;
    this.state.selectedTowerId = null;
    this.state.selectedRockId = null;
    this.state.selectedCreepId = null;
    this.selectedTowerId = null;
    this.selectedRockId = null;
    this.selectedCreepId = null;
    this.state.rocksRemoved = 0;
    this.state.downgradeUsedThisRound = false;
    this.state.tick = 0;
    this.state.wave = 0;
    this.state.lives = START_LIVES;
    this.state.gold = START_GOLD;
    this.state.totalKills = 0;
    this.state.speed = this.loadStoredSpeed();
    this.state.totalWaves = WAVES.length;
    this.state.endless = false;
    this.seed = (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
    this.wavePhase.clearGeneratedCache();
    this.state.debugWaveDef = undefined;
    this.state.gemWeaknesses = this.generateWeaknesses();
    // Reset grid
    this.state.grid = BASE.grid.map((row) => row.slice());
    renderGround(this.layers.ground, this.state.grid);
    renderCheckpoints(this.layers.checkpoints);
    this.refreshRoute();
    this.detachTelemetry?.();
    this.detachTelemetry = attachTelemetry(this);
    this.enterBuild();
  }

  restartGame(): void {
    this.newGame();
  }

  newEndlessGame(): void {
    this.newGame();
    this.state.endless = true;
  }

  continueEndless(): void {
    this.state.endless = true;
    this.enterBuild();
  }

  /** Debug mode: place gems along the blueprint maze, one wave of every creep kind. */
  newDebugGame(): void {
    this.detachTelemetry?.();
    this.detachTelemetry = undefined;
    this.state.towers = [];
    this.state.rocks = [];
    this.state.creeps = [];
    this.state.projectiles = [];
    this.vfx.clear();
    this.state.undoStack = [];
    this.state.draws = [];
    this.state.activeDrawSlot = null;
    this.state.designatedKeepTowerId = null;
    this.state.chanceTier = MAX_CHANCE_TIER;
    this.state.selectedTowerId = null;
    this.state.selectedRockId = null;
    this.state.selectedCreepId = null;
    this.selectedTowerId = null;
    this.selectedRockId = null;
    this.selectedCreepId = null;
    this.state.rocksRemoved = 0;
    this.state.downgradeUsedThisRound = false;
    this.state.tick = 0;
    this.state.wave = 1;
    this.state.lives = START_LIVES;
    this.state.gold = 99999;
    this.state.totalKills = 0;
    this.state.speed = this.loadStoredSpeed();
    this.state.totalWaves = 1;
    this.state.grid = BASE.grid.map((row) => row.slice());
    this.state.waveStats = {
      spawnedThisWave: 0,
      killedThisWave: 0,
      leakedThisWave: 0,
      totalToSpawn: 0,
    };

    const grid = this.state.grid;

    // Build gem spec list: all gem x quality + all combos at every tier
    const specs: Array<{
      gem: GemType;
      quality: Quality;
      comboKey?: string;
      upgradeTier?: number;
      isTrap?: boolean;
    }> = [];
    for (const gem of GEM_TYPES) {
      for (const q of [1, 2, 3, 4, 5] as Quality[]) {
        specs.push({ gem, quality: q });
      }
    }
    for (const combo of COMBOS) {
      for (let tier = 0; tier <= combo.upgrades.length; tier++) {
        specs.push({
          gem: combo.visualGem,
          quality: 5 as Quality,
          comboKey: combo.key,
          upgradeTier: tier,
          isTrap: combo.type === "trap",
        });
      }
    }

    // Shuffle so each debug game gets a different layout
    for (let i = specs.length - 1; i > 0; i--) {
      const j = this.rng.int(i + 1);
      [specs[i], specs[j]] = [specs[j], specs[i]];
    }

    // Simulate blueprint placement round by round (mirrors computeKeeperIndices)
    const keeperPositions: Array<{ x: number; y: number }> = [];
    const rockPositions: Array<{ x: number; y: number }> = [];

    for (const positions of MAZE_BLUEPRINT) {
      const placed: Array<{ x: number; y: number; localIdx: number }> = [];
      for (let i = 0; i < positions.length; i++) {
        const [x, y] = positions[i];
        let valid = true;
        for (let dy = 0; dy < 2; dy++)
          for (let dx = 0; dx < 2; dx++)
            if (grid[y + dy]?.[x + dx] !== Cell.Grass) valid = false;
        if (!valid) continue;
        for (let dy = 0; dy < 2; dy++)
          for (let dx = 0; dx < 2; dx++) grid[y + dy][x + dx] = Cell.Tower;
        placed.push({ x, y, localIdx: placed.length });
      }
      if (placed.length === 0) continue;

      const segments = findRoute(grid);
      const flat = segments ? flattenRoute(segments) : [];
      const routeSet = new Set(flat.map((p) => `${p.x},${p.y}`));

      let bestLocalIdx = 0;
      let bestExp = -1;
      for (let i = 0; i < placed.length; i++) {
        const exp = exposureAt(placed[i].x, placed[i].y, routeSet);
        if (exp > bestExp) {
          bestExp = exp;
          bestLocalIdx = i;
        }
      }

      keeperPositions.push({
        x: placed[bestLocalIdx].x,
        y: placed[bestLocalIdx].y,
      });
      for (let i = 0; i < placed.length; i++) {
        if (i === bestLocalIdx) continue;
        const { x, y } = placed[i];
        for (let dy = 0; dy < 2; dy++)
          for (let dx = 0; dx < 2; dx++) grid[y + dy][x + dx] = Cell.Rock;
        rockPositions.push({ x, y });
      }
    }

    // Assign gems: keepers first (best exposure), then overflow into rock slots
    const allSlots = [...keeperPositions, ...rockPositions];
    let specIdx = 0;
    for (const pos of allSlots) {
      if (specIdx >= specs.length) break;
      const spec = specs[specIdx];
      const cellType = spec.isTrap ? Cell.Trap : Cell.Tower;
      for (let dy = 0; dy < 2; dy++)
        for (let dx = 0; dx < 2; dx++) grid[pos.y + dy][pos.x + dx] = cellType;
      this.state.towers.push({
        id: this.nextId(),
        x: pos.x,
        y: pos.y,
        gem: spec.gem,
        quality: spec.quality,
        comboKey: spec.comboKey,
        upgradeTier: spec.upgradeTier,
        lastFireTick: 0,
        kills: 0,
        totalDamage: 0,
        waveDamage: 0,
        placedWave: this.state.wave,
        isTrap: spec.isTrap || undefined,
        targetingPriority: this.initialTowerTargeting(spec.gem, spec.comboKey),
      });
      specIdx++;
    }

    // Remaining slots stay as rocks
    for (let i = specIdx; i < allSlots.length; i++) {
      const pos = allSlots[i];
      for (let dy = 0; dy < 2; dy++)
        for (let dx = 0; dx < 2; dx++) grid[pos.y + dy][pos.x + dx] = Cell.Rock;
      this.state.rocks.push({
        x: pos.x,
        y: pos.y,
        id: this.nextId(),
        placedAtBuildOfWave: 1,
      });
    }

    // Debug wave: wave 50 gestation boss
    const debugWave: WaveDef = {
      number: 50,
      groups: [
        {
          kind: "gestation",
          count: 1,
          hp: 40000,
          bounty: 40,
          slowResist: 0.7,
          stunResist: 0.7,
          armor: 25,
          payload: [
            { kind: "chrysalid", count: 5, hp: 90000, bounty: 29, armor: 10 },
            { kind: "mender", count: 3, hp: 90000, bounty: 29, armor: 10 },
            // { kind: "amalgam", count: 5, hp: 86000, bounty: 29, armor: 20 },
            // { kind: "mender", count: 3, hp: 133000, bounty: 29, armor: 20 },
          ],
        },
      ],
      interval: 0.5,
      bonus: 100,
    };
    this.state.debugWaveDef = debugWave;

    renderGround(this.layers.ground, this.state.grid);
    renderCheckpoints(this.layers.checkpoints);
    this.refreshRoute();

    this.state.designatedKeepTowerId = this.state.towers[0]?.id ?? null;
    this.state.phase = "build";
    this.bus.emit("phase:enter", { phase: "build" });
    this.bus.emit("gold:change", { gold: this.state.gold });
    this.bus.emit("lives:change", { lives: this.state.lives });
  }

  enterBuild(): void {
    this.state.phase = "build";
    this.state.wave += 1;
    this.state.undoStack = [];
    this.state.designatedKeepTowerId = null;
    this.state.keptTowerIdThisRound = null;
    this.state.downgradeUsedThisRound = false;
    this.state.waveStats = {
      spawnedThisWave: 0,
      killedThisWave: 0,
      leakedThisWave: 0,
      totalToSpawn: 0,
    };
    this.buildPhase.onEnter();
    if (this.aiSpectator) this.state.speed = 1;
    this.bus.emit("phase:enter", { phase: "build" });
    if (this.aiSpectator && this.aiDriver) {
      setTimeout(() => this.aiDriver?.runBuild(this), 0);
    }
  }

  enterWave(): void {
    if (this.state.phase !== "build") return;
    // A recipe combine during the round can auto-conclude (clears draws, sets keep).
    const roundConcluded =
      this.state.draws.length === 0 &&
      this.state.designatedKeepTowerId !== null;
    if (!roundConcluded) {
      if (!this.buildPhase.ready()) {
        this.bus.emit("toast", {
          kind: "error",
          text: "Place all 5 gems first",
        });
        return;
      }
      if (this.state.designatedKeepTowerId === null) {
        if (this.creativeMode) {
          const firstDraw = this.state.draws.find(
            (d) => d.placedTowerId !== null,
          );
          if (firstDraw)
            this.state.designatedKeepTowerId = firstDraw.placedTowerId;
        } else {
          this.bus.emit("toast", {
            kind: "error",
            text: "Mark one gem to keep first",
          });
          return;
        }
      }
      this.buildPhase.applyKeepAndRock();
    }
    this.state.keptTowerIdThisRound = this.state.designatedKeepTowerId;
    this.state.draws = [];
    this.state.designatedKeepTowerId = null;
    this.state.phase = "wave";
    this.state.undoStack = [];
    this.state.activeDrawSlot = null;
    for (const t of this.state.towers) t.waveDamage = 0;
    if (this.aiSpectator) this.state.speed = 8;
    this.wavePhase.onEnter(this.state.wave);
    this.bus.emit("phase:enter", { phase: "wave" });
    this.bus.emit("wave:start", { wave: this.state.wave });
  }

  handleCreepDeath(c: import("../game/State").CreepState): void {
    this.combat.handleDeathEffects(c);
    this.spawnContainerPayload(c);
  }

  private spawnContainerPayload(
    dead: import("../game/State").CreepState,
  ): void {
    spawnPayload(
      dead,
      this.state,
      this.bus,
      () => this.nextId(),
      this.pendingPayloadSpawns,
    );
  }

  private drainPendingSpawns(): void {
    drainPayload(this.state, this.bus, this.pendingPayloadSpawns);
  }

  endWave(lifeLost: number, goldEarned: number): void {
    this.pendingPayloadSpawns.length = 0;
    this.bus.emit("wave:end", { wave: this.state.wave, lifeLost, goldEarned });
    if (this.state.lives <= 0) {
      this.state.phase = "gameover";
      this.bus.emit("phase:enter", { phase: "gameover" });
      return;
    }
    if (this.state.wave >= this.state.totalWaves && !this.state.endless) {
      this.state.phase = "victory";
      this.bus.emit("phase:enter", { phase: "victory" });
      return;
    }
    this.enterBuild();
  }

  /**
   * Refreshes state.routeSegments / flatRoute. Pass `precomputed` to reuse a
   * route that was already solved for the identical grid (e.g. the placement
   * validation route in BuildPhase.place), skipping a redundant A* sweep.
   */
  refreshRoute(precomputed?: ReturnType<typeof findRoute>): boolean {
    const route = precomputed ?? findRoute(this.state.grid);
    if (!route) {
      return false;
    }
    this.state.routeSegments = route;
    this.state.flatRoute = flattenRoute(route);
    if (this.state.airRoute.length === 0) {
      this.state.airRoute = buildAirRoute();
    }
    if (this._recording) {
      // Skip Pixi rendering during AI recording pass
    } else if (this.pathVizEnabled) {
      renderPathTrace(this.layers.pathOverlay, route, this.state.phase);
    } else {
      this.layers.pathOverlay.removeChildren();
    }
    return true;
  }

  togglePathViz(): void {
    this.pathVizEnabled = !this.pathVizEnabled;
    try {
      localStorage.setItem("gemtd:path-viz", this.pathVizEnabled ? "1" : "0");
    } catch {
      /* private mode */
    }
    this.refreshRoute();
  }

  toggleCreativeMode(): void {
    this.creativeMode = !this.creativeMode;
    if (this.creativeMode) {
      this.detachTelemetry?.();
      this.detachTelemetry = undefined;
    }
  }

  async toggleBlueprint(): Promise<void> {
    this.blueprintMode = !this.blueprintMode;
    if (this.blueprintMode && !this.blueprint) {
      const bp: Blueprint = {
        rounds: MAZE_BLUEPRINT as [number, number][][],
        removals: MAZE_REMOVALS as [number, number][][],
      };
      bp.keeperIndices = MAZE_KEEPER_INDICES
        ? Array.from(MAZE_KEEPER_INDICES)
        : computeKeeperIndices(bp);
      this.blueprint = bp;
    }
  }

  get entityIdCounter(): number {
    return this.nextEntityId;
  }
  set entityIdCounter(v: number) {
    this.nextEntityId = v;
  }

  nextId(): number {
    return this.nextEntityId++;
  }

  selectTower(id: number | null): void {
    this.selectedTowerId = id;
    this.state.selectedTowerId = id;
    this.hoveredTowerId = null;
    if (id !== null) {
      this.selectedRockId = null;
      this.state.selectedRockId = null;
      this.selectedCreepId = null;
      this.state.selectedCreepId = null;
    }
  }

  selectRock(id: number | null): void {
    this.selectedRockId = id;
    this.state.selectedRockId = id;
    if (id !== null) {
      this.selectedTowerId = null;
      this.state.selectedTowerId = null;
      this.selectedCreepId = null;
      this.state.selectedCreepId = null;
    }
  }

  selectCreep(id: number | null): void {
    this.selectedCreepId = id;
    this.state.selectedCreepId = id;
    if (id !== null) {
      this.selectedTowerId = null;
      this.state.selectedTowerId = null;
      this.selectedRockId = null;
      this.state.selectedRockId = null;
    }
  }

  rockRemovalCost(): number {
    return 0;
  }

  /** True when the rock can be removed: not currently in the same build phase it was placed in. */
  canRemoveRock(rockId: number): boolean {
    const rock = this.state.rocks.find((r) => r.id === rockId);
    if (!rock) return false;
    return !(
      this.state.phase === "build" &&
      this.state.wave === rock.placedAtBuildOfWave
    );
  }

  cmdRemoveRock(rockId: number): boolean {
    const state = this.state;
    const cells = state.rocks.filter((r) => r.id === rockId);
    if (cells.length === 0) return false;
    if (!this.canRemoveRock(rockId)) {
      this.bus.emit("toast", {
        kind: "error",
        text: "Wait until this round ends",
      });
      return false;
    }
    const removedCells = cells.map((c) => ({ ...c }));
    for (const c of cells) {
      state.grid[c.y][c.x] = Cell.Grass;
    }
    state.rocks = state.rocks.filter((r) => r.id !== rockId);
    state.rocksRemoved += 1;
    if (state.selectedRockId === rockId) this.selectRock(null);
    this.refreshRoute();
    state.undoStack.push({
      description: "Remove rock",
      undo: () => {
        for (const c of removedCells) {
          state.grid[c.y][c.x] = Cell.Rock;
          state.rocks.push(c);
        }
        state.rocksRemoved -= 1;
        this.refreshRoute();
      },
    });
    this.bus.emit("rock:remove", { id: rockId, cost: 0 });
    this.bus.emit("toast", { kind: "good", text: "Rock cleared" });
    return true;
  }

  setSpeed(s: SpeedMultiplier): void {
    this.state.speed = s;
    try {
      localStorage.setItem("gemtd:sim-speed", String(s));
    } catch {
      /* private mode */
    }
  }

  private loadStoredSpeed(): number {
    try {
      const v = localStorage.getItem("gemtd:sim-speed");
      if (v !== null) {
        const n = Number(v);
        if (SPEEDS.includes(n as SpeedMultiplier)) return n;
      }
    } catch {
      /* private mode */
    }
    return 2;
  }

  /** Center the board container in the canvas host. */
  layoutBoard(canvasW: number, canvasH: number): void {
    const boardW = this.state.grid[0].length * FINE_TILE;
    const boardH = this.state.grid.length * FINE_TILE;
    this.board.x = Math.round((canvasW - boardW) / 2);
    this.board.y = Math.round((canvasH - boardH) / 2);
  }

  /** Pixi ticker callback. */
  private tick(_ticker: Ticker): void {
    this.drainAccumulator();
    this.renderEntities();
  }

  /** Advance the sim to match elapsed wall-clock time. */
  private generateWeaknesses(): GemType[] {
    const combatGems = GEM_TYPES.filter((g) => g !== "opal");
    const blocks = Math.ceil(this.state.totalWaves / combatGems.length) + 1;
    const result: GemType[] = [];
    for (let b = 0; b < blocks; b++) {
      const block = [...combatGems];
      for (let i = block.length - 1; i > 0; i--) {
        const j = this.rng.int(i + 1);
        [block[i], block[j]] = [block[j], block[i]];
      }
      result.push(...block);
    }
    return result;
  }

  private drainAccumulator(): void {
    const now = performance.now();
    const dtMs = now - this.lastTickTime;
    this.lastTickTime = now;
    const speed = this.state.speed;
    const maxCatchUp = 120;
    const toSimulate = Math.min(maxCatchUp, (dtMs / 1000) * speed);
    this.accum += toSimulate;
    const steps = Math.floor(this.accum / SIM_DT);
    const muteThreshold = 4;
    if (steps > muteThreshold) {
      this.bus.muteVfx = true;
      const unmutAt = steps - muteThreshold;
      let i = 0;
      while (this.accum >= SIM_DT) {
        if (i === unmutAt) this.bus.muteVfx = false;
        this.simStep();
        this.accum -= SIM_DT;
        i++;
      }
      this.bus.muteVfx = false;
    } else {
      while (this.accum >= SIM_DT) {
        this.simStep();
        this.accum -= SIM_DT;
      }
    }
  }

  private simStep(): void {
    this.state.tick += 1;
    if (this.state.phase === "wave") {
      this.drainPendingSpawns();
      this.wavePhase.step();
    }
    this.combat.step();
    if (RUNES_ENABLED) this.traps.step();
  }

  private renderEntities(): void {
    this.updateHaloAlpha();
    renderTowers(
      this.layers.towers,
      this.state.towers,
      this.towerSprites,
      this.state.tick,
      this.selectedTowerId,
      this.hoveredTowerId,
    );
    renderRocks(
      this.layers.rocks,
      this.state.rocks,
      this.towerSprites,
      this.selectedRockId,
    );
    if (this.selectedCreepId !== null) {
      const sc = this.state.creeps.find((c) => c.id === this.selectedCreepId);
      if (!sc || !sc.alive) this.selectCreep(null);
    }
    renderCreeps(this.layers.creeps, this.state.creeps, this.selectedCreepId);
    renderProjectiles(this.layers.projectiles, this.state.projectiles);
    renderBeams(this.layers.projectiles, this.state.towers, this.state.creeps);
    this.vfx.render(this.layers.fx, this.state);
    if (this.blueprintMode) {
      const placedCount = this.state.draws.filter(
        (d) => d.placedTowerId !== null,
      ).length;
      renderBlueprintOverlay(
        this.layers.blueprint,
        this.blueprint,
        this.state.wave,
        this.state.phase,
        placedCount,
      );
    } else {
      renderBlueprintOverlay(this.layers.blueprint, null, 0, "", 0);
    }
    renderRangePreview(
      this.layers.preview,
      this.state,
      this.hoverTile,
      this.selectedTowerId,
    );
    renderHover(this.layers.preview, this.state, this.hoverTile);
    renderDrawPartnerHighlight(this.layers.preview, this.state);
    renderSelectedTowerPartnerHighlight(this.layers.preview, this.state);
    renderAiOverlay(this.layers.preview);
    if (this.hoverPixel) this.lastHoverPixel = this.hoverPixel;
    if (this.hoverTile) this.lastHoverTile = this.hoverTile;
    if (this.cursorGridEnabled) {
      renderCursorGrid(
        this.layers.cursorGrid,
        this.layers.ghostCell,
        this.hoverPixel ?? this.lastHoverPixel,
        this.hoverTile ?? this.lastHoverTile,
        this.haloAlpha,
        this.state.phase,
        this.state.grid,
      );
    } else {
      renderUniformGrid(this.layers.cursorGrid, this.state.phase);
    }
  }

  private updateHaloAlpha(): void {
    // 120ms fade-in, 160ms fade-out, driven by real frame delta (render-only, not sim).
    const fadeInRate = 1 / (0.12 * 60); // per-frame at 60fps
    const fadeOutRate = 1 / (0.16 * 60);
    const dt = this.app.ticker.deltaTime; // frames elapsed (typically ~1)
    if (this.hoverPresent && this.state.phase === "build") {
      this.haloAlpha = Math.min(1, this.haloAlpha + fadeInRate * dt);
    } else {
      this.haloAlpha = Math.max(0, this.haloAlpha - fadeOutRate * dt);
    }
  }

  // Public command surface used by UI / input.
  cmdPlace(x: number, y: number): boolean {
    const ok = this.buildPhase.place(x, y);
    if (ok && this.blueprintMode && this.blueprint) {
      const roundIdx = this.state.wave - 1;
      const bp = this.blueprint;
      if (roundIdx >= 0 && roundIdx < bp.rounds.length) {
        const positions = bp.rounds[roundIdx];
        const keeperIdx = bp.keeperIndices?.[roundIdx] ?? 0;
        const [kx, ky] = positions[keeperIdx];
        if (x === kx && y === ky) {
          const removals = bp.removals?.[roundIdx] ?? [];
          for (const [rx, ry] of removals) {
            const rock = this.state.rocks.find((r) => r.x === rx && r.y === ry);
            if (rock) this.cmdRemoveRock(rock.id);
          }
          const rest = positions.filter((_p, i) => i !== keeperIdx);
          for (let i = rest.length - 1; i > 0; i--) {
            const j = this.rng.int(i + 1);
            [rest[i], rest[j]] = [rest[j], rest[i]];
          }
          for (const [px, py] of rest) {
            this.buildPhase.place(px, py);
          }
        }
      }
    }
    return ok;
  }
  cmdUndo(): boolean {
    return this.buildPhase.undo();
  }
  cmdStartWave(): void {
    this.enterWave();
  }
  /** Roll the round's draws. Only valid in build phase before placement begins. */
  cmdStartPlacement(): boolean {
    const state = this.state;
    if (state.phase !== "build") return false;
    if (state.draws.length > 0 || state.designatedKeepTowerId !== null)
      return false;
    this.buildPhase.rollDraws();
    if (this.blueprintMode && this.blueprint) {
      this.clearBlueprintRocks();
    }
    return true;
  }

  private clearBlueprintRocks(): void {
    const bp = this.blueprint!;
    const roundIdx = this.state.wave - 1;
    if (roundIdx < 0 || roundIdx >= bp.rounds.length) return;

    const removals = bp.removals?.[roundIdx] ?? [];
    for (const [rx, ry] of removals) {
      const rock = this.state.rocks.find((r) => r.x === rx && r.y === ry);
      if (rock) this.cmdRemoveRock(rock.id);
    }

    const keeperIdx = bp.keeperIndices?.[roundIdx] ?? 0;
    const positions = bp.rounds[roundIdx];
    if (keeperIdx >= 0 && keeperIdx < positions.length) {
      const [kx, ky] = positions[keeperIdx];
      const removed = new Set<number>();
      for (const [dx, dy] of [
        [0, 0],
        [1, 0],
        [0, 1],
        [1, 1],
      ] as const) {
        const rock = this.state.rocks.find(
          (r) => r.x === kx + dx && r.y === ky + dy,
        );
        if (rock && !removed.has(rock.id)) {
          removed.add(rock.id);
          this.cmdRemoveRock(rock.id);
        }
      }
    }
  }
  cmdSetActiveSlot(slotId: number): void {
    this.buildPhase.setActiveSlot(slotId);
  }
  cmdCycleActiveSlot(direction: 1 | -1): void {
    const state = this.state;
    if (state.draws.length === 0) return;
    const unplaced = state.draws.filter((d) => d.placedTowerId === null);
    if (unplaced.length === 0) return;
    const sorted = unplaced.map((d) => d.slotId).sort((a, b) => a - b);
    const cur = state.activeDrawSlot ?? sorted[0];
    const idx = sorted.indexOf(cur);
    const nextIdx =
      idx < 0 ? 0 : (idx + direction + sorted.length) % sorted.length;
    this.buildPhase.setActiveSlot(sorted[nextIdx]);
  }
  cmdDesignateKeep(towerId: number): boolean {
    const state = this.state;
    if (state.phase !== "build") return false;
    const isCurrentDraw = state.draws.some((d) => d.placedTowerId === towerId);
    if (!isCurrentDraw) {
      this.bus.emit("toast", {
        kind: "error",
        text: "Mark a gem from this round only",
      });
      return false;
    }
    state.designatedKeepTowerId = towerId;
    this.bus.emit("draws:change", {});
    if (allDrawsPlaced(state)) {
      this.enterWave();
    }
    return true;
  }
  cmdPlaceCreativeRock(x: number, y: number): boolean {
    const state = this.state;
    if (state.phase !== "build" || !this.creativeMode) return false;
    if (x < 0 || y < 0 || x + 1 >= GRID_W || y + 1 >= GRID_H) return false;
    const offsets: ReadonlyArray<readonly [number, number]> = [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ];
    for (const [dx, dy] of offsets) {
      if (!isBuildable(state.grid[y + dy][x + dx])) return false;
    }
    const tentative = new Set<number>();
    for (const [dx, dy] of offsets) tentative.add((y + dy) * GRID_W + (x + dx));
    if (!findRoute(state.grid, tentative)) {
      this.bus.emit("toast", { kind: "error", text: "Would block path" });
      return false;
    }
    const id = this.nextId();
    for (const [dx, dy] of offsets) {
      state.grid[y + dy][x + dx] = Cell.Rock;
      state.rocks.push({
        x: x + dx,
        y: y + dy,
        id,
        placedAtBuildOfWave: state.wave,
      });
    }
    this.refreshRoute();
    state.undoStack.push({
      description: "Place creative rock",
      undo: () => {
        for (const [dx, dy] of offsets) {
          state.grid[y + dy][x + dx] = Cell.Grass;
        }
        state.rocks = state.rocks.filter((r) => r.id !== id);
        this.refreshRoute();
      },
    });
    return true;
  }

  cmdTakeOver(): void {
    if (!this.aiSpectator) return;
    this.aiSpectator = false;
    this.aiDriver?.cancel();
    this.aiDriver = null;
    this.state.speed = 1;
    this.bus.emit("ai:clear", {});
    this.bus.emit("toast", { kind: "good", text: "You have taken over" });
  }

  cmdUpgradeChanceTier(): boolean {
    const state = this.state;
    if (state.chanceTier >= MAX_CHANCE_TIER) {
      this.bus.emit("toast", { kind: "info", text: "Chance tier maxed" });
      return false;
    }
    const cost = CHANCE_TIER_UPGRADE_COST[state.chanceTier];
    if (state.gold < cost) {
      this.bus.emit("toast", { kind: "error", text: `Need ${cost}g` });
      return false;
    }
    state.gold -= cost;
    state.chanceTier += 1;
    this.bus.emit("gold:change", { gold: state.gold });
    this.bus.emit("chance:upgrade", { tier: state.chanceTier, cost });
    this.bus.emit("toast", {
      kind: "good",
      text: `Chance tier → L${state.chanceTier}`,
    });
    return true;
  }
  cmdCombine(towerIds: number[]): boolean {
    return this.buildPhase.combine(towerIds);
  }
  cmdDowngrade(towerId: number): boolean {
    return this.buildPhase.downgrade(towerId);
  }
  /**
   * Overwrite the tower's targeting-priority list. Used by the inspector
   * editor for add/remove/reorder. Caller is responsible for the
   * HP-orderings-are-terminal invariant (UI enforces it).
   */
  cmdSetTowerTargeting(
    towerId: number,
    list: TargetingPriority[],
  ): boolean {
    const tower = this.state.towers.find((t) => t.id === towerId);
    if (!tower) return false;
    tower.targetingPriority = list;
    return true;
  }
  /**
   * Copy the source tower's targeting priority to every other placed tower
   * of the same type AND remember it as the per-type default so any
   * future tower of the same type inherits it. Per-type defaults supersede
   * `globalTargetingDefault`. "Same type" = same `comboKey` for combo
   * towers, or same `gem` (any quality) for basic towers.
   */
  cmdApplyTargetingToType(towerId: number): number {
    const src = this.state.towers.find((t) => t.id === towerId);
    if (!src) return 0;
    const list = src.targetingPriority ?? [];
    // Stash the per-type default so future placements/combines inherit it.
    if (src.comboKey) {
      this.state.comboTargetingDefaults ??= {};
      this.state.comboTargetingDefaults[src.comboKey] = structuredClone(list);
    } else {
      this.state.gemTargetingDefaults ??= {};
      this.state.gemTargetingDefaults[src.gem] = structuredClone(list);
    }
    let applied = 0;
    for (const t of this.state.towers) {
      if (t.id === src.id) continue;
      if (src.comboKey) {
        if (t.comboKey !== src.comboKey) continue;
      } else {
        if (t.comboKey || t.gem !== src.gem) continue;
      }
      t.targetingPriority = structuredClone(list);
      applied++;
    }
    return applied;
  }
  /**
   * Copy the source tower's targeting priority to *every* other placed
   * tower on the board AND remember it as the global default, so any
   * tower placed or combined afterwards inherits the same list.
   */
  cmdApplyTargetingGlobally(towerId: number): number {
    const src = this.state.towers.find((t) => t.id === towerId);
    if (!src) return 0;
    const list = src.targetingPriority ?? [];
    this.state.globalTargetingDefault = structuredClone(list);
    let applied = 0;
    for (const t of this.state.towers) {
      if (t.id === src.id) continue;
      t.targetingPriority = structuredClone(list);
      applied++;
    }
    return applied;
  }
  /** Thin wrapper around the shared helper — see `pickInitialTowerTargeting`. */
  initialTowerTargeting(gem: GemType, comboKey?: string): TargetingPriority[] {
    return pickInitialTowerTargeting(this.state, gem, comboKey);
  }
  cmdUpgradeTower(towerId: number): boolean {
    const state = this.state;
    const tower = state.towers.find((t) => t.id === towerId);
    if (!tower || !tower.comboKey) {
      this.bus.emit("toast", { kind: "error", text: "Not a special tower" });
      return false;
    }
    const combo = COMBO_BY_NAME.get(tower.comboKey!);
    if (!combo) return false;
    const currentTier = tower.upgradeTier ?? 0;
    const upgrade = nextUpgrade(combo, currentTier);
    if (!upgrade) {
      this.bus.emit("toast", { kind: "info", text: "Already max tier" });
      return false;
    }
    if (state.gold < upgrade.cost) {
      this.bus.emit("toast", { kind: "error", text: `Need ${upgrade.cost}g` });
      return false;
    }
    state.gold -= upgrade.cost;
    tower.upgradeTier = currentTier + 1;
    invalidateTowerStats(tower);
    this.bus.emit("gold:change", { gold: state.gold });
    this.bus.emit("tower:upgrade", {
      id: towerId,
      comboKey: tower.comboKey,
      tier: tower.upgradeTier,
    });
    this.bus.emit("toast", {
      kind: "good",
      text: `Upgraded to ${upgrade.name}`,
    });
    return true;
  }
}
